import httpClient from '@utils/httpClient';

import { IndividualObjective, TeamObjective, OrgObjective, Period, Milestone } from '../types';

const BASE_URL = '/okr'; // Using prefix defined in routes: router.use('/okr', okrRoutes)

export const okrApi = {
    // Periods
    getActivePeriod: async () => {
        const response = await httpClient.get<{ success: boolean; data: Period }>(`${BASE_URL}/periods/active`);
        return response.data;
    },

    getPeriods: async () => {
        const response = await httpClient.get<{ success: boolean; data: Period[] }>(`${BASE_URL}/periods`);
        return response.data;
    },

    createPeriod: async (name: string, startDate: string, endDate: string) => {
        return httpClient.post(`${BASE_URL}/periods`, { name, start_date: startDate, end_date: endDate });
    },

    setActivePeriod: async (periodId: string) => {
        return httpClient.put(`${BASE_URL}/periods/${periodId}/activate`, {});
    },

    updatePeriod: async (periodId: string, name: string, startDate: string, endDate: string) => {
        return httpClient.put(`${BASE_URL}/periods/${periodId}`, { name, start_date: startDate, end_date: endDate });
    },

    getPeriodCascadeCounts: async (periodId: string) => {
        const response = await httpClient.get<{
            success: boolean;
            data: {
                period_name: string;
                orgObjectives: number;
                teamObjectives: number;
                individualObjectives: number;
                milestones: number;
                notes: number;
            };
        }>(`${BASE_URL}/periods/${periodId}/cascade-counts`);
        return response.data;
    },

    deletePeriod: async (periodId: string) => {
        return httpClient.delete(`${BASE_URL}/periods/${periodId}`);
    },

    // Period Locking
    getPeriodLockStatus: async (periodId: string) => {
        const response = await httpClient.get<{
            success: boolean;
            data: { period_id: string; is_locked: boolean; status: string };
        }>(`${BASE_URL}/periods/${periodId}/lock`);
        return response.data;
    },

    lockPeriod: async (periodId: string) => {
        const response = await httpClient.post<{ success: boolean; message: string; is_locked: boolean }>(
            `${BASE_URL}/periods/${periodId}/lock`,
            {}
        );
        return response.is_locked;
    },

    unlockPeriod: async (periodId: string) => {
        const response = await httpClient.delete<{ success: boolean; message: string; is_locked: boolean }>(
            `${BASE_URL}/periods/${periodId}/lock`
        );
        return response.is_locked;
    },

    // Goal Locking (Per-Org Objective)
    toggleOrgObjectiveLock: async (id: string) => {
        const response = await httpClient.put<{ success: boolean; message: string; is_locked: number }>(
            `${BASE_URL}/objectives/org/${id}/lock`
        );
        return response;
    },

    // Views
    getMyOkrs: async (userId?: string) => {
        const response = await httpClient.get<{ success: boolean; data: IndividualObjective[] }>(`${BASE_URL}/my`, {
            params: { user_id: userId }
        });
        return response.data;
    },

    getTeamOkrs: async (deptId?: number) => {
        const response = await httpClient.get<{ success: boolean; data: TeamObjective[] }>(`${BASE_URL}/team`, {
            params: { department_id: deptId }
        });
        return response.data;
    },

    getOrgOkrs: async (periodId?: string) => {
        const response = await httpClient.get<{ success: boolean; data: OrgObjective[] }>(`${BASE_URL}/org`, {
            params: { period_id: periodId }
        });
        return response.data;
    },

    getAtomicMap: async () => {
        const response = await httpClient.get<{ success: boolean; data: any }>(`${BASE_URL}/atomic-map`);
        return response.data;
    },

    // Creation
    createOrgObjective: async (period_id: string, title: string, description: string, department_ids?: number[]) => {
        return httpClient.post(`${BASE_URL}/objectives/org`, { period_id, title, description, department_ids });
    },

    createTeamObjective: async (org_objective_id: string, department_id: number, title: string, description?: string, weight: number = 0.25) => {
        return httpClient.post(`${BASE_URL}/objectives/team`, { org_objective_id, department_id, title, description, weight });
    },

    createIndividualObjective: async (teamObjId: string, title: string) => {
        return httpClient.post(`${BASE_URL}/objectives/individual`, { team_objective_id: teamObjId, title });
    },

    createMilestone: async (objectiveId: string, title: string, dueDate?: string, startDate?: string, assignedTo?: string, status?: string) => {
        return httpClient.post(`${BASE_URL}/milestones`, { objective_id: objectiveId, title, due_date: dueDate, start_date: startDate, assigned_to: assignedTo, status });
    },

    updateMilestoneStatus: async (id: string, status: string) => {
        return httpClient.put(`${BASE_URL}/milestones/${id}/status`, { status });
    },

    deleteMilestone: async (id: string) => {
        return httpClient.delete(`${BASE_URL}/milestones/${id}`);
    },

    createWeeklyUpdate: async (payload: { objective_id: string; status: string; comment: string; blockers: string }) => {
        return httpClient.post(`${BASE_URL}/weekly-updates`, payload);
    },

    assignEmployeeToTeam: async (teamObjId: string, employee_id: string, title: string, weight: number = 1.0) => {
        return httpClient.post(`${BASE_URL}/objectives/team/${teamObjId}/assign-employee`, { employee_id, title, weight });
    },

    // Manager assigns a task/objective to an employee
    assignTaskToEmployee: async (teamObjId: string, employeeId: string, title: string) => {
        return httpClient.post(`${BASE_URL}/objectives/assign-task`, { team_objective_id: teamObjId, employee_id: employeeId, title });
    },

    // Updates
    updateOrgObjective: async (id: string, title: string, department_ids?: number[]) => {
        return httpClient.put(`${BASE_URL}/objectives/org/${id}`, { title, department_ids });
    },
    updateOrgObjectiveScore: async (id: string, score: number) => {
        return httpClient.put(`${BASE_URL}/objectives/org/${id}/score`, { score });
    },
    updateTeamObjective: async (id: string, title: string, weight?: number, defined_measure?: string, comment?: string) => {
        const payload: any = { title };
        if (weight !== undefined) {
            payload.weight = weight;
        }
        if (defined_measure !== undefined) {
            payload.defined_measure = defined_measure;
        }
        if (comment !== undefined) {
            payload.comment = comment;
        }
        return httpClient.put(`${BASE_URL}/objectives/team/${id}`, payload);
    },
    updateIndividualObjective: async (id: string, title: string, startDate?: string, dueDate?: string, comment?: string, status?: string) => {
        const payload: any = { title };
        if (startDate !== undefined) payload.start_date = startDate;
        if (dueDate !== undefined) payload.due_date = dueDate;
        if (comment !== undefined) payload.comment = comment;
        if (status !== undefined) payload.status = status;
        return httpClient.put(`${BASE_URL}/objectives/individual/${id}`, payload);
    },
    updateMilestone: async (id: string, title: string, startDate?: string, dueDate?: string, assignedTo?: string, status?: string) => {
        return httpClient.put(`${BASE_URL}/milestones/${id}`, { title, start_date: startDate, due_date: dueDate, assigned_to: assignedTo, status });
    },

    // Admin Operations
    seedPeriods: async () => {
        const response = await httpClient.post<{ success: boolean; message: string; count: number }>(
            `${BASE_URL}/seed-periods`,
            {}
        );
        return response;
    },

    // Admin Status Control
    updateObjectiveStatus: async (id: string, type: 'org' | 'team' | 'individual', status: string) => {
        return httpClient.put(`${BASE_URL}/objectives/${type}/${id}/status`, { status });
    },

    updateObjectiveStatusAndScore: async (id: string, type: 'org' | 'team' | 'individual', status: string, score: number) => {
        return httpClient.put(`${BASE_URL}/objectives/${type}/${id}/status-score`, { status, score });
    },

    deleteObjective: async (id: string, type: 'org' | 'team' | 'individual') => {
        return httpClient.delete(`${BASE_URL}/objectives/${type}/${id}`);
    },

    // Notes / Feedback
    addNote: async (objectiveId: string, content: string, mentions?: Array<{ type: 'user' | 'team'; id: string }>) => {
        return httpClient.post(`${BASE_URL}/notes`, { objective_id: objectiveId, content, mentions });
    },

    getNotes: async (objectiveId: string) => {
        const response = await httpClient.get<{ success: boolean; data: any[] }>(`${BASE_URL}/notes/${objectiveId}`);
        return response.data;
    },

    updateNote: async (noteId: string, content: string, mentions?: Array<{ type: 'user' | 'team'; id: string }>) => {
        return httpClient.put(`${BASE_URL}/notes/${noteId}`, { content, mentions });
    },

    deleteNote: async (noteId: string) => {
        return httpClient.delete(`${BASE_URL}/notes/${noteId}`);
    },

    // Departments
    getDepartments: async () => {
        const response = await httpClient.get<{ success: boolean; data: any[] }>('/hr/employees/departments/list');
        return response.data;
    },

    // Get all employees/users for atomic map
    getEmployees: async () => {
        const response = await httpClient.get<{ success: boolean; data: any[] }>('/hr/employees?noPagination=true');
        return response.data;
    }
};
